// packages/shared-hooks/src/requests/useRequestManagerOptions.ts
// Issue #421. Source for the request-list manager picker, replacing
// `useUsersList` to decouple the picker from the `users.manage`-gated
// `GET /api/users` endpoint. The new endpoint
// `GET /api/requests/manager-options` is gated by `requests.read` and
// scoped server-side to managers visible under the caller's
// request-domain visibility predicate.
import { useQuery } from '@tanstack/react-query';
import { RequestManagerOptionsResponseSchema, type RequestManagerOption } from '@magimp/shared';

import { useApiClient } from '../api/context.js';

/**
 * Optional knobs passed through to `useQuery` for callers that need to gate
 * the request (e.g. the issue #630 manager-validation flow disables the
 * picker query until the actor has the right permission, and caps retries
 * at 1 so a slow backend never traps the page in a long retry spinner).
 *
 * INVARIANT: the surface stays narrow on purpose. Exposing a full
 * `UseQueryOptions` shape would invite callers to override `queryKey` /
 * `queryFn` and silently break the shared-cache contract this hook owns.
 */
export interface UseRequestManagerOptionsOptions {
  enabled?: boolean;
  retry?: number | boolean;
}

export function useRequestManagerOptions(options: UseRequestManagerOptionsOptions = {}) {
  const client = useApiClient();
  return useQuery<RequestManagerOption[]>({
    queryKey: ['requests', 'manager-options'],
    queryFn: ({ signal }) =>
      client.get<RequestManagerOption[]>(
        '/requests/manager-options',
        { signal },
        RequestManagerOptionsResponseSchema,
      ),
    enabled: options.enabled ?? true,
    ...(options.retry !== undefined ? { retry: options.retry } : {}),
  });
}
